import { get, isString, map, reduce } from 'lodash';
import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import DropZone from '../../DropZone';
import styles from './pdf-styles.module.scss';
import Button from '../../Button';
import PropTypes from 'prop-types';
import { ReactComponent as RemoveIcon } from './imgs/ic-close.svg';
import { ReactComponent as AddDocIcon } from './imgs/ic-add-circle-outline.svg';
import * as classnames from 'classnames';
import * as ReactModal from 'react-modal';
import BUTTON_COLORS from '../../Button/colors';
import apiService from '../../../services/apiService';
import Loading from '../../Loading';
import { getFileSize } from 'utils/format';
import { convertMbtoBytes } from 'modules/Taps/services/FileService';
import { ThumbComponentM } from './Thumbs';
import { PreviewComponent } from './Previews';
import { setFileSize, validateFiles } from './FileUploadService';
import {
  fileUploadModalActionTypes,
  fileUploadModalInitialState,
  fileUploadModalReducer,
} from './store';
import { useSelector } from 'react-redux';
import { selectRouter } from 'modules/App/utils/selectors';
import toastService from 'modules/Toasts';
import { INTERNAL_SERVER_ERROR } from 'constants/responseErrors';
import { FileUploadModalContext } from './FileUploadModalContext';

const DEFAULT_ATTACH_ERROR = 'Произошла ошибка загрузки файлов';
const TOASTS_GROUP = 'FILES_UPLOAD_MODAL';
const customStyles = {
  overlay: {
    padding: '24px',
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 99,
  },
  content: {
    height: '100%',
    backgroundColor: 'white',
    overflow: 'auto',
  },
};

function ErrorModalMessage() {
  const { errors } = useContext(FileUploadModalContext);

  useEffect(() => {
    if (errors && errors.error) {
      const {
        error: { title, details, originalError },
      } = errors;

      // Server errors with 500 status are processing with errorHandlerInterceptor
      if (get(originalError, 'response.status') !== INTERNAL_SERVER_ERROR) {
        toastService.error(isString(title) ? title : DEFAULT_ATTACH_ERROR, details, {
          autoClose: false,
          group: TOASTS_GROUP,
        });
      }

      return () => {
        toastService.dismissGroup(TOASTS_GROUP);
      };
    }
  }, [errors]);

  return null;
}

export const DropZoneBlock = () => {
  const {
    onAddFiles,
    files,
    selectedDocument,
    showFileInPreview,
    onRemoveDocument,
    options,
  } = useContext(FileUploadModalContext);
  return (
    <>
      <DropZone
        onDrop={(file) => onAddFiles(map(file, 'fileObject'), options)}
        accept={options.accept}
        openDialogOnClick={true}
        maxFileSizeMb={20}
        className={'flex-fill'}
      >
        {() => (
          <div className="d-flex flex-wrap w-100 flex-fill">
            {files.map((file) => {
              const selectedDocumentId = get(selectedDocument, 'documentId');
              const isActive = selectedDocumentId === file.documentId;
              const className = classnames(styles.thumb, {
                [styles.active]: isActive,
              });
              return (
                <div
                  key={file.documentId}
                  className={className}
                  title={file.fileName}
                  onClick={(e) => {
                    e.stopPropagation();
                    showFileInPreview(file.documentId);
                  }}
                >
                  <ThumbComponentM fileBlob={file.fileBlob} isActive={isActive} />
                  <div
                    className={styles.removeIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveDocument(file.documentId);
                    }}
                  >
                    <RemoveIcon />
                  </div>
                </div>
              );
            })}
            <button className={styles.uploadButton}>
              <AddDocIcon />
            </button>
          </div>
        )}
      </DropZone>
    </>
  );
};

export const FileUploadModalContent = function (
  { applyDocuments, getDocument, uploadDocument, removeDocument },
  ref,
) {
  const [state, dispatch] = useReducer(fileUploadModalReducer, fileUploadModalInitialState);
  const router = useSelector(selectRouter);

  const { loading, isOpenModal, selectedDocumentId, files, errors, options } = state;
  const setLoading = useCallback((payload) => {
    dispatch({ type: fileUploadModalActionTypes.setLoading, payload });
  }, []);

  const setIsOpenModal = useCallback((payload) => {
    dispatch({ type: fileUploadModalActionTypes.setOpenModal, payload });
  }, []);

  const setSelectedDocumentId = useCallback((payload) => {
    dispatch({ type: fileUploadModalActionTypes.setSelectedDocumentId, payload });
  }, []);

  const selectedDocument = useMemo(() => {
    return files.find((file) => file.documentId === selectedDocumentId) || get(files, 0);
  }, [files, selectedDocumentId]);

  const setError = useCallback((payload) => {
    dispatch({ type: fileUploadModalActionTypes.setError, payload });
  }, []);

  const setOptions = useCallback((payload) => {
    dispatch({ type: fileUploadModalActionTypes.setOptions, payload });
  }, []);

  const resetStore = useCallback((payload) => {
    dispatch({ type: fileUploadModalActionTypes.reset, payload });
  }, []);

  const addFiles = useCallback((payload) => {
    dispatch({ type: fileUploadModalActionTypes.addFiles, payload });
  }, []);

  const replaceFiles = useCallback((payload) => {
    dispatch({ type: fileUploadModalActionTypes.replaceFiles, payload });
  }, []);

  const removeFile = useCallback((payload) => {
    dispatch({ type: fileUploadModalActionTypes.removeFile, payload });
  }, []);

  const allFilesAmountByte = reduce(
    files,
    (sum, file) => {
      return sum + file.fileBlob.size;
    },
    0,
  );

  const alreadyUploaded = reduce(
    options.files,
    (sum, file) => {
      return +file.size + sum;
    },
    0,
  );

  const remainingFilesAmountByte = getFileSize(
    convertMbtoBytes(options.allFilesMaxSizeMb) - allFilesAmountByte - alreadyUploaded,
  ).label;

  const uploadPromise = useRef();

  const onAddFiles = useCallback(
    async (acceptedFiles, options) => {
      setLoading(true);
      setError(null);
      try {
        const { isMultiple } = options;
        const filesToProcess = isMultiple ? acceptedFiles : acceptedFiles.splice(0, 1);

        setLoading(true);
        const processedFiles = [];
        await Promise.all(
          filesToProcess.map(async (file) => {
            try {
              const response = await uploadDocument(file);
              const resData = response.data;
              const fileBlob = await getDocument({ documentId: resData.documentId });

              const objectParameters = {
                scale: 1,
                qualityLevel: 3,
              };

              const nFile = setFileSize({
                ...objectParameters,
                fileObject: fileBlob,
                id: resData.documentId,
                name: file.name,
                origFileSize: file.size,
                fileName: file.name,
                fileBlob,
                documentId: resData.documentId,
              });
              processedFiles.push(nFile);
            } catch (e) {
              setError({
                error: {
                  title: DEFAULT_ATTACH_ERROR,
                  details: get(e, 'response.data.message'),
                  originalError: e,
                },
              });
            }
          }),
        );

        if (processedFiles.length) {
          // i use it to update selected document with files in order to updated selected document in context because they rely on each other

          if (!isMultiple && selectedDocumentId) {
            await removeDocument(selectedDocumentId);
          }
          isMultiple ? addFiles(processedFiles) : replaceFiles(processedFiles);
        }
      } catch (e) {
        setError({ error: { title: DEFAULT_ATTACH_ERROR } });
      } finally {
        setLoading(false);
      }
    },
    [files, selectedDocumentId],
  );

  const showFileInPreview = useCallback(async (documentId) => {
    setSelectedDocumentId(documentId);
  }, []);

  const onRemoveDocument = useCallback(async (documentId) => {
    setLoading(true);
    setError(null);
    try {
      await removeDocument(documentId);
      removeFile(documentId);
    } catch (e) {
      setError({
        error: {
          title: DEFAULT_ATTACH_ERROR,
          details: isString(e) ? e : get(e, 'response.data.message'),
        },
      });
    }

    setLoading(false);
  }, []);

  const onApply = useCallback(async () => {
    setLoading(true);
    try {
      await validateFiles(files, options);
      const res = await applyDocuments(files);
      get(uploadPromise, 'current.resolve') && uploadPromise.current.resolve(res);
      resetStore();
    } catch (e) {
      setError({
        error: {
          title: DEFAULT_ATTACH_ERROR,
          details: isString(e) ? e : get(e, 'response.data.message'),
        },
      });
    }
    setLoading(false);
  }, [files, options]);

  const onDecline = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all(files.map(async (file) => removeDocument(file.documentId)));
      get(uploadPromise, 'current.reject') && uploadPromise.current.resolve([]);
      resetStore();
    } catch (e) {
      setError({
        error: {
          title: DEFAULT_ATTACH_ERROR,
          details: isString(e) ? e : get(e, 'response.data.message'),
        },
      });
    }

    setLoading(false);
  }, [files]);

  useImperativeHandle(ref, () => ({
    uploadFiles: (
      acceptedFiles,
      options = {
        accept: '',
        allFilesMaxSizeMb: 20,
        maxFileSizeMb: 2,
        openDialogOnClick: true,
      },
    ) => {
      return new Promise((resolve, reject) => {
        const uploadCallBack = async function () {
          resetStore();
          setOptions(options);
          setIsOpenModal(true);
          await onAddFiles([...acceptedFiles], options);
          uploadPromise.current = {
            resolve,
            reject,
          };
        };
        uploadCallBack();
      });
    },
  }));

  const allowedFormats = Array.isArray(options.accept)
    ? options.accept.join(', ')
    : options.accept.split(',').join(', ');

  useEffect(() => {
    if (!files.length) {
      onDecline();
    }
  }, [files]);

  useEffect(() => {
    if (isOpenModal) {
      resetStore();
      setIsOpenModal(false);
    }
  }, [router.pathname]);
  return (
    <FileUploadModalContext.Provider
      value={{
        getDocument,
        onDecline,
        onRemoveDocument,
        showFileInPreview,
        files,
        addFiles,
        replaceFiles,
        onAddFiles,
        setLoading,
        selectedDocument,
        selectedDocumentId,
        setSelectedDocumentId,
        options,
        errors,
        setError,
      }}
    >
      <ReactModal style={customStyles} className={`boo`} isOpen={isOpenModal}>
        <div className={`row no-gutters ${styles.fullHeight}`}>
          {loading ? (
            <Loading />
          ) : (
            <>
              <div
                className="col-12 col-md-7 position-relative d-flex justify-content-center "
                style={{ maxHeight: '100%' }}
              >
                {selectedDocument && (
                  <PreviewComponent
                    key={selectedDocumentId.documentId}
                    className={`${styles.pdfPreviewWrapper}`}
                  />
                )}
              </div>
              <div className="col-12 col-md-5 d-flex flex-column justify-content-between  ">
                <div className="d-flex position-relative w-100 pt-2p0 pl-2p0 pr-2p0 flex-fill flex-column">
                  <h2 className="pb-2p0">Сжатие файлов</h2>
                  {loading ? <Loading /> : <DropZoneBlock />}
                </div>
                <div>
                  <div className="Caption disabled text-center p-2">
                    {errors && <ErrorModalMessage />}
                    Осталось {remainingFilesAmountByte} из {options.allFilesMaxSizeMb}Мб
                    <br />
                    Допустимые форматы: {allowedFormats}
                  </div>
                  <div className={`${styles.actionButton} p-sm-2p0 p-3 flex-column flex-sm-row`}>
                    <Button color={BUTTON_COLORS.WHITE_BLUE} onClick={onDecline} hasWrapper={false}>
                      Отменить
                    </Button>
                    <Button color={BUTTON_COLORS.ORANGE} onClick={onApply} hasWrapper={false}>
                      Применить
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </ReactModal>
    </FileUploadModalContext.Provider>
  );
};

export const FileUploadModal = forwardRef(FileUploadModalContent);

FileUploadModal.defaultProps = {
  getDocument: async ({ documentId, quality, rotate }) => {
    const response = await apiService.mmServer.getDocument(documentId, quality, rotate, {
      responseType: 'blob',
    });
    return response.data;
  },
  uploadDocument: async (file) => {
    return apiService.mmServer.uploadDocument(file);
  },
  removeDocument: async (docId) => {
    const response = await apiService.mmServer.removeDocument(docId);
    return response.data;
  },
  applyDocuments: async (files) => {
    const documentIds = map(files, 'documentId');
    const response = await apiService.mmServer.applyDocuments(documentIds);
    files.forEach((file) => {
      file.fileObject = file.fileBlob;
      file.serverFileName = response.data[file.documentId];
      file.fileLink = response.data[file.documentId];
      file.name = file.fileName;
      file.id = file.documentId;
      file.size = file.fileBlob.size;

      const sizeObject = getFileSize(file.size);
      file.sizeToDisplay = sizeObject.label;
    });
    return files;
  },
};
FileUploadModalContent.propTypes = {
  getDocument: PropTypes.func,
  uploadDocument: PropTypes.func,
  removeDocument: PropTypes.func,
  applyDocuments: PropTypes.func,
};
